import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { API_ENDPOINTS, apiClient } from '../config/api';
import './AgentVerification.css';
import stateCityData from '../data/state_city.json';
import { formatUSPhoneInput, toE164US } from '../utils/phone';
import {
  isValidLicenseNumber,
  TX_LICENSE_PATTERN,
  TX_FIRM_LICENSE_TYPES,
  TX_BROKER_LICENSE_TYPE,
  affiliationPersonName,
  canonicalLicenseNumber,
} from '../utils/license';

// License validation rules by state (all 50 US states + DC)
const LICENSE_VALIDATION_RULES = {
  AL: { pattern: /^\d{5,6}$/,              placeholder: '12345',        hint: '5 to 6 digits',                        example: '12345 or 123456' },
  AK: { pattern: /^\d{6,8}$/,              placeholder: '123456',       hint: '6 to 8 digits',                        example: '123456' },
  AZ: { pattern: /^(SA|BR)?\d{9}$/i,       placeholder: 'SA123456789',  hint: '9 digits, optional SA/BR prefix',      example: 'SA123456789 or 123456789' },
  AR: { pattern: /^(SB|AB|PB)?\d{7,8}$/i,  placeholder: 'SB1234567',    hint: '7-8 digits, optional SB/AB/PB prefix', example: 'SB1234567' },
  CA: { pattern: /^(SB|AB|PB)?\d{7,9}$/i,  placeholder: 'SB1234567',    hint: '7-9 digits',                           example: 'SB1234567' },
  // CO DORA license numbers always carry a 2-letter level prefix (no bare-digit
  // format exists in the state registry) — prefix is required, not optional,
  // unlike most other states here. All 9 real DORA prefixes: FA/EA/IA (individual
  // brokers, by level) and ER/EI/II/EC/IC/IR (responsible brokers + companies).
  CO: { pattern: /^(FA|EA|IA|ER|EI|II|EC|IC|IR)\d{9}$/i, placeholder: 'FA100012345', hint: '2-letter prefix + 9 digits', example: 'FA100012345' },
  CT: { pattern: /^\d{9}$/,                placeholder: '123456789',    hint: '9 digits',                             example: '123456789' },
  DE: { pattern: /^[A-Z0-9]{7,8}$/i,       placeholder: 'RA123456',     hint: '7-8 alphanumeric characters',          example: 'RA123456' },
  DC: { pattern: /^\d{6,8}$/,              placeholder: '123456',       hint: '6 to 8 digits',                        example: '123456' },
  FL: { pattern: /^(SL|BK)\d{7}$/i,        placeholder: 'SL1234567',    hint: 'SL or BK followed by 7 digits',        example: 'SL1234567 or BK1234567' },
  GA: { pattern: /^\d{6}$/,                placeholder: '123456',       hint: '6 digits',                             example: '123456' },
  HI: { pattern: /^(RS|RB)?\d{6,8}$/i,     placeholder: 'RS12345678',   hint: '6-8 digits, optional RS/RB prefix',    example: 'RS12345678' },
  ID: { pattern: /^(SP|AB)?\d{7,8}$/i,     placeholder: 'SP1234567',    hint: '7-8 digits, optional SP/AB prefix',    example: 'SP1234567' },
  IL: { pattern: /^\d{6,9}$/,              placeholder: '471123456',    hint: '6 to 9 digits',                        example: '471123456' },
  IN: { pattern: /^(RB)?\d{9,10}$/i,       placeholder: 'RB14000123',   hint: '9-10 digits, optional RB prefix',      example: 'RB14000123' },
  IA: { pattern: /^\d{6,8}$/,              placeholder: '12345678',     hint: '6 to 8 digits',                        example: '12345678' },
  KS: { pattern: /^(SP|BR|00)?\d{5,8}$/i,  placeholder: 'SP00012345',   hint: '5-8 digits, optional prefix',          example: 'SP00012345' },
  KY: { pattern: /^\d{5,6}$/,              placeholder: '12345',        hint: '5 to 6 digits',                        example: '12345' },
  LA: { pattern: /^\d{6,10}$/,             placeholder: '1234567890',   hint: '6 to 10 digits',                       example: '1234567890' },
  ME: { pattern: /^[A-Z]{2,3}\d{4,6}$/i,   placeholder: 'AER123456',    hint: '2-3 letters + 4-6 digits',             example: 'AER123456' },
  MD: { pattern: /^\d{6,8}$/,              placeholder: '12345678',     hint: '6 to 8 digits',                        example: '12345678' },
  MA: { pattern: /^\d{6,9}$/,              placeholder: '123456789',    hint: '6 to 9 digits',                        example: '123456789' },
  MI: { pattern: /^\d{10}$/,               placeholder: '6501123456',   hint: '10 digits',                            example: '6501123456' },
  MN: { pattern: /^\d{6,8}$/,              placeholder: '12345678',     hint: '6 to 8 digits',                        example: '12345678' },
  MS: { pattern: /^[SB]?\d{5,6}$/i,        placeholder: 'S12345',       hint: '5-6 digits, optional S/B prefix',      example: 'S12345' },
  MO: { pattern: /^\d{6,10}$/,             placeholder: '1234567890',   hint: '6 to 10 digits',                       example: '1234567890' },
  MT: { pattern: /^(RRE|RBS)?\d{4,6}$/i,   placeholder: 'RRE1234',      hint: '4-6 digits, optional RRE/RBS prefix',  example: 'RRE1234' },
  NE: { pattern: /^\d{6,10}$/,             placeholder: '1234567890',   hint: '6 to 10 digits',                       example: '1234567890' },
  NV: { pattern: /^(S|B|BS|PM)?\d{6,10}$/i,placeholder: 'S.0012345',    hint: '6-10 digits, optional prefix',         example: 'S.0012345' },
  NH: { pattern: /^\d{6,8}$/,              placeholder: '12345678',     hint: '6 to 8 digits',                        example: '12345678' },
  NJ: { pattern: /^\d{7,9}$/,              placeholder: '123456789',    hint: '7 to 9 digits',                        example: '123456789' },
  NM: { pattern: /^\d{6,8}$/,              placeholder: '12345678',     hint: '6 to 8 digits',                        example: '12345678' },
  NY: { pattern: /^([A-Z]-?\d{7}-?\d-?\d|\d{10})$/i, placeholder: 'H-1234567-8-9', hint: 'Letter-7digits-digit-digit or 10 digits', example: 'H-1234567-8-9 or 1012345678' },
  NC: { pattern: /^\d{6,8}$/,              placeholder: '12345678',     hint: '6 to 8 digits',                        example: '12345678' },
  ND: { pattern: /^\d{5,7}$/,              placeholder: '1234567',      hint: '5 to 7 digits',                        example: '1234567' },
  OH: { pattern: /^\d{8,10}$/,             placeholder: '1234567890',   hint: '8 to 10 digits',                       example: '1234567890' },
  OK: { pattern: /^\d{6,9}$/,              placeholder: '123456789',    hint: '6 to 9 digits',                        example: '123456789' },
  OR: { pattern: /^\d{9}$/,                placeholder: '123456789',    hint: '9 digits',                             example: '123456789' },
  PA: { pattern: /^(RS|RB|RM|RA)?\d{6,9}$/i, placeholder: 'RS123456',  hint: '6-9 digits, optional RS/RB/RM/RA prefix', example: 'RS123456' },
  RI: { pattern: /^(RES|REB)?\d{5,8}$/i,   placeholder: 'RES12345',     hint: '5-8 digits, optional RES/REB prefix',  example: 'RES12345' },
  SC: { pattern: /^\d{5,6}$/,              placeholder: '12345',        hint: '5 to 6 digits',                        example: '12345' },
  SD: { pattern: /^\d{5,7}$/,              placeholder: '1234567',      hint: '5 to 7 digits',                        example: '1234567' },
  TN: { pattern: /^\d{6,9}$/,              placeholder: '123456789',    hint: '6 to 9 digits',                        example: '123456789' },
  // TREC: 4-8 digits + SA (sales agent) / B (broker) / BB (broker company) suffix.
  // Inputs strip hyphens as typed, so "859229-SA" is entered as "859229SA".
  TX: { pattern: TX_LICENSE_PATTERN,    placeholder: '859229SA',     hint: '4-8 digits + SA, B or BB suffix',      example: '859229SA or 736484B' },
  UT: { pattern: /^\d{7,10}$/,             placeholder: '1234567890',   hint: '7 to 10 digits',                       example: '1234567890' },
  VT: { pattern: /^[A-Z0-9]{6,10}$/i,      placeholder: '082123456',    hint: '6-10 alphanumeric',                    example: '082.0123456' },
  VA: { pattern: /^\d{9}$/,                placeholder: '123456789',    hint: '9 digits',                             example: '123456789' },
  WA: { pattern: /^\d{6,8}$/,              placeholder: '12345678',     hint: '6 to 8 digits',                        example: '12345678' },
  WV: { pattern: /^[A-Z]{0,2}\d{6,8}$/i,   placeholder: 'WV12345678',   hint: '6-8 digits, optional WV prefix',       example: 'WV12345678' },
  WI: { pattern: /^\d{6,8}$/,              placeholder: '12345678',     hint: '6 to 8 digits',                        example: '12345678' },
  WY: { pattern: /^\d{5,7}$/,              placeholder: '1234567',      hint: '5 to 7 digits',                        example: '1234567' },
};

// Map technical API errors to user-friendly messages (mirrors mobile)
const mapVerificationError = (error) => {
  const detail = (
    error?.response?.data?.verificationDetails ||
    error?.response?.data?.message ||
    error?.message || ''
  ).toLowerCase();
  if (detail.includes('jwt') || detail.includes('token') || detail.includes('auth'))
    return 'Please enter the correct licence number. The entered number is invalid.';
  if (detail.includes('not found') || detail.includes('no record') || detail.includes('no match'))
    return 'License number was not found in the state registry.';
  if (detail.includes('expir'))
    return 'This license appears to be expired.';
  if (detail.includes('inactive') || detail.includes('suspended') || detail.includes('revoked'))
    return 'This license is not currently active.';
  if (detail.includes('format'))
    return 'The license number format appears to be incorrect.';
  if (detail.includes('not available') || detail.includes('not supported'))
    return 'License verification is not available for this state yet.';
  return 'Unable to verify your license. Please check your details and try again.';
};

const AgentVerificationPage = () => {
  const navigate  = useNavigate();
  const location  = useLocation();

  // ── Invite / route data ───────────────────────────────────────────────────
  const inviteData = (() => {
    if (location.state?.inviteData) return location.state.inviteData;
    const s = sessionStorage.getItem('inviteData');
    return s ? JSON.parse(s) : {};
  })();

  // ── License verification fields ───────────────────────────────────────────
  const [selectedState,  setSelectedState]  = useState('');
  const [licenseNumber,  setLicenseNumber]  = useState('');
  const [narId,          setNarId]          = useState('');

  // ── Brokerage information fields (shown after verification succeeds) ───────
  const [brokerageName,          setBrokerageName]          = useState('');
  const [brokerageLicenseNumber, setBrokerageLicenseNumber] = useState('');
  const [brokerageAddress,       setBrokerageAddress]       = useState('');
  const [brokeragePhone,         setBrokeragePhone]         = useState('');
  const [brokerOfRecordName,     setBrokerOfRecordName]     = useState('');

  // ── Validation state machine: 'idle'|'validating'|'valid'|'invalid' ───────
  const [validationState,   setValidationState]   = useState('idle');
  const [validationMessage, setValidationMessage] = useState('');
  const [verificationResponse, setVerificationResponse] = useState(null);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [isAddingRole, setIsAddingRole] = useState(false);
  const [submitError,  setSubmitError]  = useState('');
  const [displayName,  setDisplayName]  = useState('there');

  const debounceRef = useRef(null);

  // ── Pre-fill from invite data + logged-in user profile ────────────────────
  useEffect(() => {
    const storedUser    = JSON.parse(localStorage.getItem('user') || '{}');
    const userFirstName = storedUser.firstName || '';
    const userLastName  = storedUser.lastName  || '';

    setDisplayName(inviteData.firstName || userFirstName || 'there');

    setSelectedState (inviteData.state        || '');
    setLicenseNumber (inviteData.licenseNumber || inviteData.license || '');
    setNarId         (inviteData.narId         || inviteData.nar     || '');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  // ── License validation helpers ────────────────────────────────────────────
  const getLicensePlaceholder = () => {
    const v = LICENSE_VALIDATION_RULES[selectedState];
    return v ? `License Number (e.g., ${v.placeholder})` : 'License Number';
  };

  const getLicenseHint = () => {
    const v = LICENSE_VALIDATION_RULES[selectedState];
    return v ? `Format: ${v.hint} (e.g., ${v.example})` : '';
  };

  // Per-state format check (falls back to the shared min-length check for any
  // state without a defined rule). Previously LICENSE_VALIDATION_RULES was
  // display-only (hint text) and never actually gated verification, so a
  // bare-digit CO number with no prefix would silently hit the API instead
  // of being rejected client-side.
  const passesStateFormat = (value, state) => {
    const rule = LICENSE_VALIDATION_RULES[state];
    if (!rule) return isValidLicenseNumber(value);
    return rule.pattern.test((value || '').trim());
  };

  // ── Core: call verify-license API ─────────────────────────────────────────
  const verifyLicense = useCallback(async (license, state) => {
    if (!license || !state) return;

    setValidationState('validating');
    setValidationMessage('');
    setVerificationResponse(null);

    try {
      const storedUser   = JSON.parse(localStorage.getItem('user') || '{}');
      const userFullName = `${storedUser.firstName || ''} ${storedUser.lastName || ''}`.trim();
      const agentName    = inviteData.firstName
        ? `${inviteData.firstName} ${inviteData.lastName || ''}`.trim()
        : (userFullName || 'Rialty User');

      const res  = await apiClient.post(API_ENDPOINTS.verifyLicense, {
        licenseNumber: license.trim(),
        state,
        role: 'AGENT',
        agentName,
        email:       inviteData.email || storedUser.email || undefined,
        phoneNumber: inviteData.phone || storedUser.phone || undefined,
        narId:       narId.trim()     || undefined,
      });

      const data    = res.data?.data || res.data;
      const isValid = data?.isValid === true || data?.status === 'valid' || data?.status === 'VERIFIED';

      setVerificationResponse(data);

      if (isValid) {
        setValidationState('valid');
        setValidationMessage('License verified successfully!');
      } else {
        setValidationState('invalid');
        setValidationMessage(
          data?.verificationDetails || data?.message ||
          'License could not be verified. Please check your details.'
        );
      }
    } catch (error) {
      console.error('[AgentVerification] verify-license error:', error);
      if (error?.response?.data?.validationErrors) {
        setValidationState('invalid');
        setValidationMessage('Please fill in all required fields correctly.');
        return;
      }
      setValidationState('invalid');
      setValidationMessage(
        error?.response?.status === 404
          ? 'License number was not found in the state registry.'
          : mapVerificationError(error)
      );
    }
  }, [inviteData, narId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Field change handlers ─────────────────────────────────────────────────
  const handleStateChange = (e) => {
    setSelectedState(e.target.value);
    setLicenseNumber('');
    setValidationState('idle');
    setValidationMessage('');
    setVerificationResponse(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
  };

  const handleLicenseChange = (e) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
    setLicenseNumber(value);
    setValidationState('idle');
    setValidationMessage('');
    setVerificationResponse(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Auto-verify after 1 second once state is selected and the number passes
    // that state's format check (falls back to the shared min-length check).
    if (selectedState && passesStateFormat(value, selectedState)) {
      debounceRef.current = setTimeout(() => verifyLicense(value, selectedState), 1000);
    }
  };

  const handleVerifyClick = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    verifyLicense(licenseNumber, selectedState);
  };

  // ── Derived UI flags ──────────────────────────────────────────────────────
  const canVerify        = selectedState && passesStateFormat(licenseNumber, selectedState);
  const showVerifyBtn    = canVerify && validationState !== 'validating' && validationState !== 'valid';
  // Brokerage section is only relevant for already-registered users switching roles.
  // New users arriving via invite links don't need to fill in brokerage details here —
  // that data is collected during registration.
  const showBrokerageSection = validationState === 'valid' && !!inviteData.alreadyRegistered;
  const isButtonDisabled = validationState !== 'valid' || isAddingRole;

  // ── Submit (Continue) ─────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isButtonDisabled) return;

    setIsAddingRole(true);
    setSubmitError('');

    const firebaseToken = localStorage.getItem('firebaseToken');
    const storedUser    = JSON.parse(localStorage.getItem('user') || '{}');
    const isLoggedIn    = !!firebaseToken;

    if (inviteData.alreadyRegistered || isLoggedIn) {
      // ── Existing / Switch Role user: update agent profile via API ──────────
      try {
        const userId = storedUser.id || storedUser.firebaseUid || '';

        if (userId) {
          await apiClient.put(
            API_ENDPOINTS.agentProfile(userId),
            {
              licenseState:           selectedState,
              licenseNumber:          canonicalLicenseNumber(licenseNumber, verificationResponse, selectedState),
              narId:                  narId.trim()                  || undefined,
              brokerageName:          brokerageName.trim()          || undefined,
              brokerageLicenseNumber: brokerageLicenseNumber.trim() || undefined,
              brokerOfRecordName:     brokerOfRecordName.trim()     || undefined,
              brokerageAddress:       brokerageAddress.trim()       || undefined,
              brokeragePhone:         brokeragePhone.trim()         ? toE164US(brokeragePhone.trim()) : undefined,
            },
            { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
          );

          // Add AGENT role to the user's account
          try {
            await apiClient.post(
              API_ENDPOINTS.userRoles(userId),
              { role: 'AGENT' },
              { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
            );
          } catch (roleErr) {
            console.error('[AgentVerification] addRole error:', roleErr);
          }

          // Refresh profile in localStorage so Switch Role picks it up
          try {
            const profileRes = await apiClient.get(API_ENDPOINTS.userMe, {
              headers: { 'Authorization': `Bearer ${firebaseToken}` },
            });
            const updated = profileRes.data?.data || profileRes.data?.content || profileRes.data;
            if (updated && typeof updated === 'object') {
              localStorage.setItem('user', JSON.stringify({ ...storedUser, ...updated }));
            }
          } catch { /* non-critical */ }
        }

        // Accept any pending invite
        if (inviteData.key) {
          try {
            if (inviteData.type === 'user') {
              await apiClient.post(
                API_ENDPOINTS.acceptUserInvite(inviteData.key),
                {},
                { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
              );
            } else if (inviteData.type === 'transaction') {
              const userName = [storedUser.firstName, storedUser.lastName].filter(Boolean).join(' ');
              await apiClient.post(
                API_ENDPOINTS.acceptInvite,
                { invitationKey: inviteData.key, userId: storedUser.id || '', userName },
                { headers: { 'Authorization': `Bearer ${firebaseToken}` } }
              );
            }
          } catch { /* invite accept failure is non-critical */ }
        }

        sessionStorage.removeItem('inviteData');
        sessionStorage.removeItem('agentVerificationData');

        const txnId = inviteData.transactionId;
        navigate(txnId ? `/transaction/${txnId}` : '/dashboard', { replace: true });
      } catch (err) {
        console.error('[AgentVerification] profile update error:', err);
        setSubmitError(
          err?.response?.data?.message ||
          'Failed to save your agent profile. Please try again.'
        );
        setIsAddingRole(false);
      }
    } else {
      // ── New user: navigate to Register with all verification data ──────────

      // Extract the affiliated firm (Corporation / Broker / Office) from affiliations
      const firm = Array.isArray(verificationResponse?.affiliations)
        ? verificationResponse.affiliations.find(
            a => a.licenseType === 'Corporation' || a.licenseType === 'Broker' || a.licenseType === 'Office' ||
                 TX_FIRM_LICENSE_TYPES.includes(a.licenseType)
          )
        : null;

      // Extract broker of record (Officer) from the firm's own nested affiliations
      const officer = Array.isArray(firm?.affiliations)
        ? firm.affiliations.find(a => a.licenseType === 'Officer' || a.licenseType === 'Broker' || a.licenseType === TX_BROKER_LICENSE_TYPE)
        : null;
      let brokerOfRecordName = affiliationPersonName(officer);
      // TX: an agent sponsored directly by an individual broker has no nested
      // officer — the sponsoring broker is the broker of record.
      if (!brokerOfRecordName && firm?.licenseType === TX_BROKER_LICENSE_TYPE) {
        brokerOfRecordName = (firm.fullName || '').trim();
      }

      // Registry responses (source e.g. "CA_DRE"/"CO_DORA") may split a single
      // fullName into firstName/middleName/lastName server-side (see
      // AgentLicenseFullLookupService.splitFullNameIfMissing) — middleName is
      // only trustworthy from that split. ARELLO fallback responses only
      // reliably carry firstName/lastName; its middleName field is sparse and
      // unverified, so it's deliberately not used for autofill here.
      const isRegistrySource = !!verificationResponse?.source && verificationResponse.source !== 'ARELLO';

      // Licensed City: only trustworthy when the registry's mailing-address
      // state matches the licensing jurisdiction (selectedState) — otherwise
      // `city` is just an out-of-state mailing city, not where they're
      // actually licensed to practice. Left blank for the licensee to enter
      // themselves whenever we can't confirm the match. See
      // import_licenses.py's mailingState field.
      const licenseCityFromVerification =
        (isRegistrySource && verificationResponse?.mailingState === selectedState)
          ? (verificationResponse?.city || '')
          : '';
      // Same trust rule as Licensed City — a Colorado zip code from the
      // registry is only meaningful when the mailing state actually matches
      // the license jurisdiction (e.g. Loria's 52722 is an Iowa zip, not CO).
      const licenseZipFromVerification =
        (isRegistrySource && verificationResponse?.mailingState === selectedState)
          ? (verificationResponse?.zipCode || '')
          : '';

      const agentData = {
        role:                   'agent',
        state:                  selectedState,
        stateCode:              selectedState,
        licenseCityFromVerification,
        licenseZipFromVerification,
        licenseNumber:          canonicalLicenseNumber(licenseNumber, verificationResponse, selectedState),
        narId:                  narId.trim(),
        firstName:              verificationResponse?.firstName || inviteData.firstName || storedUser.firstName || '',
        middleName:             isRegistrySource ? (verificationResponse?.middleName || '') : '',
        lastName:               verificationResponse?.lastName  || inviteData.lastName  || storedUser.lastName  || '',
        email:                  verificationResponse?.email     || inviteData.email     || storedUser.email     || '',
        phone:                  verificationResponse?.phone     || inviteData.phone     || '',
        // Agent's OWN personal (mailing) address from the licence registry —
        // fills the Personal Address section on the register page, exactly as
        // the registry/ARELLO reports it (any state, not just the license
        // jurisdiction). Kept separate from the brokerage/firm address below
        // and from the Licensed Address above (mirrors mobile AgentVerificationScreen).
        agentStreet:            verificationResponse?.addressLine1 || '',
        agentCity:              verificationResponse?.city         || '',
        // Real mailing state (e.g. "IA" for an out-of-state DORA mailing
        // address) — NOT verificationResponse.state, which is always the
        // hardcoded license jurisdiction ("CO") and would silently corrupt
        // this address (e.g. "Bettendorf, CO" instead of "Bettendorf, IA").
        // ARELLO responses have no separate mailingState, so state is the
        // correct (and only) signal there.
        agentState:             verificationResponse?.mailingState || verificationResponse?.state || '',
        agentZip:               verificationResponse?.zipCode      || '',
        brokerageName:          firm?.fullName || firm?.lastName || '',
        brokerageLicenseNumber: firm?.licenseNumber || '',
        brokerOfRecordName,
        brokerageAddress:       firm?.addressLine1 || '',
        brokerageCity:          firm?.city         || '',
        brokerageState:         firm?.state        || '',
        brokerageZip:           firm?.zipCode      || '',
        brokeragePhone:         '',
        // Brokerage/firm's license type (e.g. "Corporation"), not the agent's own
        // license type — mirrors mobile's primaryAffiliation?.licenseType.
        licenseType:            firm?.licenseType                                                  || '',
        licenseExpirationDate:  verificationResponse?.expirationDate || verificationResponse?.licenseExpirationDate || '',
        verificationId:         verificationResponse?.id                                           || '',
      };
      sessionStorage.setItem('agentVerificationData', JSON.stringify(agentData));
      navigate('/register', {
        state: { agentData, inviteData: { ...inviteData, ...agentData } },
        replace: false,
      });
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="agent-verification-page">
      <div className="verification-container">
        <div className="verification-card">

          {/* Loading overlay — shown while saving agent profile */}
          {isAddingRole && (
            <div className="av-loading-overlay">
              <div className="av-loading-spinner" />
              <p className="av-loading-text">Adding Agent role...</p>
            </div>
          )}

          {/* Header */}
          <div className="verification-header">
            <p className="greeting-text">Hi {displayName},</p>
            <h1 className="verification-title">You're almost in!</h1>
            <p className="verification-subtitle">
              Let's verify your real estate license to get started.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="verification-form">

            {/* ── State selector ── */}
            <div className="form-group">
              <select
                value={selectedState}
                onChange={handleStateChange}
                className={`form-select ${!selectedState ? 'placeholder' : ''}`}
                required
              >
                <option value="">Select State</option>
                {stateCityData.map((item) => (
                  <option key={item.stateCode} value={item.stateCode}>
                    {item.state} ({item.stateCode})
                  </option>
                ))}
              </select>
            </div>

            {/* ── License Number ── */}
            <div className="form-group">
              <div className="license-input-wrapper">
                <input
                  type="text"
                  placeholder={getLicensePlaceholder()}
                  value={licenseNumber}
                  onChange={handleLicenseChange}
                  maxLength={20}
                  className={`form-input${validationState === 'invalid' ? ' input-error' : ''}${validationState === 'valid' ? ' input-success' : ''}`}
                />
                {validationState === 'validating' && (
                  <span className="license-verifying">Verifying...</span>
                )}
              </div>
              {selectedState && validationState === 'idle' && (
                licenseNumber.trim().length > 0 && !passesStateFormat(licenseNumber, selectedState) ? (
                  <span className="license-error">Please enter a valid license number.</span>
                ) : getLicenseHint() ? (
                  <span className="license-hint">{getLicenseHint()}</span>
                ) : null
              )}
            </div>

            {/* ── NAR ID ── */}
            <div className="form-group">
              <input
                type="text"
                placeholder="NAR ID (Optional)"
                value={narId}
                onChange={(e) => setNarId(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
                maxLength={20}
                className="form-input"
              />
            </div>

            {/* ── Manual "Verify License" button — shown when idle/invalid and eligible ── */}
            {showVerifyBtn && (
              <button
                type="button"
                className="av-verify-btn"
                onClick={handleVerifyClick}
              >
                Verify License
              </button>
            )}

            {/* ── Validation result row ── */}
            {validationState !== 'idle' && validationMessage && (
              <div className={`av-validation-row av-validation-${validationState}`}>
                <span className="av-validation-icon">
                  {validationState === 'valid'      ? '✓'
                  : validationState === 'validating' ? '…'
                  :                                   '⚠'}
                </span>
                <span className="av-validation-text">
                  {validationMessage}
                  {validationState === 'valid' && verificationResponse?.licenseType && (
                    <span style={{ display: 'block', fontSize: '12px', marginTop: 3, opacity: 0.85 }}>
                      License Type: {verificationResponse.licenseType}
                    </span>
                  )}
                </span>
              </div>
            )}

            {/* ── Brokerage Information — only for Switch Role (already-registered) users ── */}
            {showBrokerageSection && (
              <div className="av-brokerage-section">
                <h3 className="av-section-header">Brokerage Information (Optional)</h3>

                <input
                  type="text"
                  placeholder="Brokerage Name"
                  value={brokerageName}
                  onChange={(e) => setBrokerageName(e.target.value)}
                  maxLength={100}
                  className="form-input"
                />

                <input
                  type="text"
                  placeholder="Brokerage License Number"
                  value={brokerageLicenseNumber}
                  onChange={(e) => setBrokerageLicenseNumber(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
                  maxLength={20}
                  className="form-input"
                />

                <input
                  type="text"
                  placeholder="Brokerage Address"
                  value={brokerageAddress}
                  onChange={(e) => setBrokerageAddress(e.target.value)}
                  maxLength={200}
                  className="form-input"
                />

                <input
                  type="tel"
                  placeholder="Brokerage Phone"
                  value={brokeragePhone}
                  onChange={(e) => setBrokeragePhone(formatUSPhoneInput(e.target.value))}
                  maxLength={20}
                  className="form-input"
                />

                <input
                  type="text"
                  placeholder="Broker of Record Name"
                  value={brokerOfRecordName}
                  onChange={(e) => setBrokerOfRecordName(e.target.value)}
                  maxLength={100}
                  className="form-input"
                />
              </div>
            )}

            {/* ── Submit error ── */}
            {submitError && (
              <div className="status-message error">{submitError}</div>
            )}

            {/* ── Continue button ── */}
            <button
              type="submit"
              className="btn-continue"
              disabled={isButtonDisabled}
            >
              {isAddingRole ? 'Processing...' : 'Continue'}
            </button>

          </form>

          <div className="verification-footer">
            <p className="terms-text">
              By continuing, you agree to our{' '}
              <Link to="/terms" className="terms-link">Terms</Link>
              {' '}and{' '}
              <Link to="/privacy" className="terms-link">Privacy policy</Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentVerificationPage;
